/**
 * Named labels (ADR-039): one org workspace's labels — the Labels page's
 * data and writes, and the document page's label panel.
 *
 * A named label restricts a document ON TOP OF its tier: reading it needs the
 * tier AND a place on the label. Owners and admins are on no label by
 * default. The server decides every answer; these hooks only fetch and send.
 *
 * Every write invalidates the whole workspace family
 * (`queryKeys.vault.namedLabelsAll`), because one write changes the list,
 * the readers, the requests and the caller's own place at once. A write that
 * changes a document also invalidates that document.
 *
 * SECURITY: the step-up writes (adding a reader, an owner or admin adding
 * themselves, approving a request) send the person's password and TOTP code,
 * so they go through the untraced `stepUpFetch` — never the traced `api`
 * client, whose request log the report-a-problem dialog exposes.
 */
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  vaultNamedLabelPath,
  type VaultNamedLabelDto,
  type VaultNamedLabelReaderDto,
  type VaultNamedLabelRequestDto,
} from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { stepUpFetch } from '@/lib/step-up-fetch';
import type { StepUpCredentials } from '../components/StepUpDialog';

const proof = (creds: StepUpCredentials) => ({
  currentPassword: creds.currentPassword,
  ...(creds.totpCode ? { totpCode: creds.totpCode } : {}),
});

/** The error code a request was refused with (on the error, or leading its message). */
export function namedLabelErrorCode(err: unknown): string | null {
  const code = (err as { code?: unknown })?.code;
  if (typeof code === 'string') return code;
  const message = err instanceof Error ? err.message : '';
  return /^([A-Z][A-Z0-9_]+):/.exec(message)?.[1] ?? null;
}

const MESSAGES: Record<string, string> = {
  NAMED_LABELS_NOT_AVAILABLE: 'Named labels aren’t available in this workspace yet.',
  NAMED_LABEL_NAME_TAKEN: 'There’s already a label with that name here.',
  NAMED_LABEL_LIMIT: 'This workspace has as many labels as it can hold.',
  NAMED_LABEL_FORBIDDEN: 'You don’t have permission to do that on this label.',
  NAMED_LABEL_HOLDS_DOCUMENTS:
    'Documents still have this label. Deprecate it instead, so no new documents get it.',
  NAMED_LABEL_IS_FOLDER_DEFAULT:
    'A folder gives this label to new documents. Change that folder first.',
  NAMED_LABEL_LAST_MANAGER: 'A label needs at least one manager.',
  NAMED_LABEL_MANAGER_REQUIRED: 'Only a manager of this label can decide that.',
  NAMED_LABEL_SECOND_PERSON_REQUIRED: 'Someone other than the person who asked has to decide.',
  NAMED_LABEL_REQUEST_LAPSED: 'That request has expired.',
  NAMED_LABEL_REQUEST_OPEN: 'A request about this is already waiting.',
  NAMED_LABEL_REQUEST_NOT_REOPENABLE:
    'That request can’t be asked again — they may have left the group, the group may be off the label, they can already read it, or you no longer manage the group.',
  NAMED_LABEL_GROUP_CHANGED:
    'The group’s members changed while you were looking. Review them again.',
  NAMED_LABEL_DEPRECATED: 'That label is deprecated, so no new documents can get it.',
  NAMED_LABEL_NOT_APPLICABLE: 'That label can’t go on this document.',
  REDISCLOSURE_REQUIRED:
    'This document has a label. A manager of the label has to approve that change — ask for it below.',
  DROP_BOX_CONFIRMATION_REQUIRED: 'Confirm filing into the drop box first.',
  NAMED_LABEL_ALREADY_READER: 'They’re already on this label.',
  NAMED_LABEL_READER_LIMIT: 'This label has as many people as it can hold.',
  NAMED_LABEL_SAME: 'The document already has that label.',
  DROP_BOX_RATE_LIMITED: 'You’ve filed as many documents into this drop box as you can today.',
  DROP_BOX_FOLDER_DEFAULT: 'A drop box can’t be a folder’s default label.',
  DOCUMENT_IN_TRASH: 'Restore the document from the Trash first.',
  DOCUMENT_CHANGED: 'The document changed just now. Try again.',
  NOT_CURRENT_VERSION: 'This is an earlier version. Open the current one to change its label.',
  FOLDER_CHANGED: 'The folder changed just now. Try again.',
  ACCESS_BUSY: 'Someone else is changing this label right now. Try again in a moment.',
  RATE_LIMIT: 'Too many label changes. Wait a minute and try again.',
  MFA_ENROLLMENT_REQUIRED: 'Turn on two-step verification in your account settings first.',
  HUMAN_SESSION_REQUIRED: 'Do this from the Fias web app.',
  NOT_FOUND: 'Not found — it may have been removed, or you’re not on it.',
};

/** Plain words for a refused label request. */
export function describeNamedLabelError(err: unknown, fallback = 'Something went wrong.'): string {
  const code = namedLabelErrorCode(err);
  if (code && MESSAGES[code]) return MESSAGES[code];
  return err instanceof Error && err.message ? err.message : fallback;
}

function useInvalidateNamedLabels(holderId: string) {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: queryKeys.vault.namedLabelsAll(holderId) });
}

/**
 * For a write that changes which documents someone can see — leaving or
 * being removed from a label, recovering one, an approved re-disclosure: the
 * label family AND the workspace's documents, so a list or a document page
 * never shows what the server would now refuse (or hide what it now shows).
 * Document details — with their versions, history and activity, which live
 * under them — are REMOVED, not merely marked stale: a stale entry still
 * renders its name and text until a refetch replaces it.
 */
function useInvalidateNamedLabelsAndDocuments(holderId: string) {
  const qc = useQueryClient();
  return () => {
    qc.removeQueries({ queryKey: queryKeys.vaultDocuments.details() });
    return Promise.all([
      qc.invalidateQueries({ queryKey: queryKeys.vault.namedLabelsAll(holderId) }),
      qc.invalidateQueries({ queryKey: queryKeys.vaultDocuments.forHolder(holderId) }),
    ]);
  };
}

/** The caller's seat in a workspace: their tier and principal id (null while loading). */
export function useMyWorkspaceSeat(holderId: string) {
  const query = useQuery({
    queryKey: queryKeys.vault.me.memberships(),
    queryFn: () => api.listMyVaultMemberships(),
  });
  const membership = query.data?.memberships.find(
    (m) => m.holderId === holderId && m.status === 'active',
  );
  return {
    isLoading: query.isLoading,
    principalId: query.data?.principalId ?? null,
    role: (membership?.role ?? null) as 'owner' | 'admin' | 'member' | 'viewer' | null,
    holderType: membership?.holderType ?? null,
  };
}

function namedLabelsQuery(holderId: string | null) {
  return {
    queryKey: queryKeys.vault.namedLabels(holderId ?? ''),
    queryFn: () => api.listVaultNamedLabels(holderId as string),
    enabled: !!holderId,
    retry: false,
  } as const;
}

/** Owners and admins get every label; anyone else only the labels they are on (and open drop boxes). */
export function useVaultNamedLabels(holderId: string | null) {
  return useQuery({ ...namedLabelsQuery(holderId), select: (data) => data.labels });
}

/**
 * Whether to offer "New label" here: the server's answer (an owner or admin,
 * and the rollout switch admits the workspace's first label), so the screen
 * never shows a form the server would refuse.
 */
export function useCanCreateVaultNamedLabel(holderId: string | null) {
  return useQuery({ ...namedLabelsQuery(holderId), select: (data) => data.canCreate });
}

export function useVaultNamedLabelReaders(holderId: string, labelId: string | null) {
  return useQuery({
    queryKey: queryKeys.vault.namedLabelReaders(holderId, labelId ?? ''),
    queryFn: async () =>
      (await api.listVaultNamedLabelReaders(holderId, labelId as string)).readers,
    enabled: !!labelId,
    retry: false,
  });
}

export function useVaultNamedLabelAccessLog(holderId: string, labelId: string | null) {
  return useQuery({
    queryKey: queryKeys.vault.namedLabelAccessLog(holderId, labelId ?? ''),
    queryFn: async () =>
      (await api.listVaultNamedLabelAccessLog(holderId, labelId as string, { limit: 100 })).entries,
    enabled: !!labelId,
    retry: false,
  });
}

/** Requests come a page at a time (pending first, newest first); "Show more" asks for the next. */
export const NAMED_LABEL_REQUESTS_PAGE_SIZE = 100;

export function useVaultNamedLabelRequests(holderId: string) {
  const query = useInfiniteQuery({
    queryKey: queryKeys.vault.namedLabelRequests(holderId),
    queryFn: ({ pageParam }) =>
      api.listVaultNamedLabelRequests(holderId, {
        limit: NAMED_LABEL_REQUESTS_PAGE_SIZE,
        cursor: pageParam,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    retry: false,
  });
  return {
    requests: query.data?.pages.flatMap((p) => p.requests) ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    hasMore: query.hasNextPage,
    isFetchingMore: query.isFetchingNextPage,
    loadMore: () => {
      if (query.hasNextPage && !query.isFetchingNextPage) void query.fetchNextPage();
    },
  };
}

export function useVaultNamedLabelReceipts(holderId: string) {
  return useQuery({
    queryKey: queryKeys.vault.namedLabelReceipts(holderId),
    queryFn: async () => (await api.listVaultNamedLabelDropReceipts(holderId)).receipts,
    retry: false,
  });
}

export function useCreateVaultNamedLabel(holderId: string) {
  const invalidate = useInvalidateNamedLabels(holderId);
  return useMutation({
    mutationFn: async (body: {
      name: string;
      description?: string | null;
      acceptsFromNonReaders?: boolean;
    }) => (await api.createVaultNamedLabel(holderId, body)).label,
    onSuccess: invalidate,
  });
}

/**
 * Save a label's settings. `dropBox` is sent only when it changes: opening a
 * drop box is a widening (the label's PATCH), closing one a narrowing with its
 * own route that works everywhere.
 */
export function useUpdateVaultNamedLabel(holderId: string) {
  const invalidate = useInvalidateNamedLabels(holderId);
  return useMutation({
    mutationFn: async (input: {
      labelId: string;
      name?: string;
      description?: string | null;
      dropBox?: 'open' | 'close';
    }) => {
      const { labelId, dropBox, ...rest } = input;
      let label: VaultNamedLabelDto | undefined;
      if (dropBox === 'close') {
        label = (await api.closeVaultNamedLabelDropBox(holderId, labelId)).label;
      }
      if (Object.keys(rest).length > 0 || dropBox === 'open') {
        label = (
          await api.updateVaultNamedLabel(holderId, labelId, {
            ...rest,
            ...(dropBox === 'open' ? { acceptsFromNonReaders: true as const } : {}),
          })
        ).label;
      }
      return label;
    },
    onSuccess: invalidate,
  });
}

export function useDeprecateVaultNamedLabel(holderId: string) {
  const invalidate = useInvalidateNamedLabels(holderId);
  return useMutation({
    mutationFn: (labelId: string) => api.deprecateVaultNamedLabel(holderId, labelId),
    onSuccess: invalidate,
  });
}

export function useDeleteVaultNamedLabel(holderId: string) {
  const invalidate = useInvalidateNamedLabels(holderId);
  return useMutation({
    mutationFn: (labelId: string) => api.deleteVaultNamedLabel(holderId, labelId),
    onSuccess: invalidate,
  });
}

export function useSetVaultNamedLabelManager(holderId: string) {
  const invalidate = useInvalidateNamedLabels(holderId);
  return useMutation({
    mutationFn: (input: { labelId: string; principalId: string; isManager: boolean }) =>
      api.setVaultNamedLabelReaderManager(
        holderId,
        input.labelId,
        input.principalId,
        input.isManager,
      ),
    onSuccess: invalidate,
  });
}

/** Remove someone from a label — or, with your own principal id, leave it. */
export function useRemoveVaultNamedLabelReader(holderId: string) {
  const invalidate = useInvalidateNamedLabelsAndDocuments(holderId);
  return useMutation({
    mutationFn: (input: { labelId: string; principalId: string }) =>
      api.removeVaultNamedLabelReader(holderId, input.labelId, input.principalId),
    onSuccess: invalidate,
  });
}

export function useRejectVaultNamedLabelRequest(holderId: string) {
  const invalidate = useInvalidateNamedLabels(holderId);
  return useMutation({
    mutationFn: (requestId: string) => api.rejectVaultNamedLabelRequest(holderId, requestId),
    onSuccess: invalidate,
  });
}

/** Ask again for a group member a manager declined, or whose request lapsed (the requester). */
export function useAskAgainVaultNamedLabelRequest(holderId: string) {
  const invalidate = useInvalidateNamedLabels(holderId);
  return useMutation({
    mutationFn: async (requestId: string) =>
      (await api.askAgainVaultNamedLabelRequest(holderId, requestId)).request,
    onSuccess: invalidate,
  });
}

export function useWithdrawVaultNamedLabelRequest(holderId: string) {
  const invalidate = useInvalidateNamedLabels(holderId);
  return useMutation({
    mutationFn: (requestId: string) => api.withdrawVaultNamedLabelRequest(holderId, requestId),
    onSuccess: invalidate,
  });
}

/** Put a label on a document (a narrowing; a drop box needs `dropBoxConfirmed`). */
export function useApplyVaultNamedLabel(holderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      documentId: string;
      labelId: string;
      dropBoxConfirmed?: boolean;
    }) =>
      (
        await api.applyVaultNamedLabel(input.documentId, {
          holderId,
          labelId: input.labelId,
          ...(input.dropBoxConfirmed ? { dropBoxConfirmed: true } : {}),
        })
      ).document,
    onSuccess: async (_doc, input) => {
      if (input.dropBoxConfirmed) {
        // Filed into a drop box: the filer can no longer see it, so its page
        // must not keep showing the copy cached before (a refetch that 404s
        // keeps the old data).
        qc.removeQueries({ queryKey: queryKeys.vaultDocuments.detail(input.documentId) });
      }
      await Promise.all([
        qc.invalidateQueries({ queryKey: queryKeys.vault.namedLabelsAll(holderId) }),
        qc.invalidateQueries({ queryKey: queryKeys.vaultDocuments.forHolder(holderId) }),
        input.dropBoxConfirmed
          ? Promise.resolve()
          : qc.invalidateQueries({ queryKey: queryKeys.vaultDocuments.detail(input.documentId) }),
      ]);
    },
  });
}

export type VaultNamedLabelChangeRequest =
  | { kind: 'remove_label' }
  | { kind: 'move_label'; targetLabelId: string }
  | { kind: 'lower_tier'; targetSensitivity: 'standard' | 'confidential' };

/** Ask a manager of a document's label to approve a change to it. */
export function useRequestVaultNamedLabelChange(holderId: string) {
  const invalidate = useInvalidateNamedLabels(holderId);
  return useMutation({
    mutationFn: async (input: { documentId: string; change: VaultNamedLabelChangeRequest }) =>
      (await api.requestVaultNamedLabelChange(input.documentId, { holderId, ...input.change }))
        .request,
    onSuccess: invalidate,
  });
}

/**
 * Invalidate after a step-up write that bypassed react-query's mutation layer
 * (adding a reader, recovering, approving): each can change which documents
 * someone sees, so the workspace's documents are refreshed too.
 */
export function useVaultNamedLabelsInvalidator(holderId: string) {
  return useInvalidateNamedLabelsAndDocuments(holderId);
}

// ── Step-up writes: untraced, with the person's proof ────────────────────────

/** Add someone to a label (a widening a manager authorises). */
export async function addVaultNamedLabelReader(
  holderId: string,
  labelId: string,
  body: { principalId: string; isManager?: boolean; expiresAt?: string },
  creds: StepUpCredentials,
) {
  return stepUpFetch<{ reader: VaultNamedLabelReaderDto }>(
    `${vaultNamedLabelPath(holderId, labelId)}/readers`,
    { method: 'POST', body: { ...body, ...proof(creds) } },
  );
}

/** Let Fias AI read a label's documents (a manager, with step-up: a widening). */
export async function allowVaultNamedLabelAiReading(
  holderId: string,
  labelId: string,
  creds: StepUpCredentials,
) {
  return stepUpFetch<{ label: VaultNamedLabelDto }>(
    `${vaultNamedLabelPath(holderId, labelId)}/ai-reading`,
    { method: 'POST', body: proof(creds) },
  );
}

/** Stop Fias AI reading a label's documents (a narrowing: works everywhere). */
export function useStopVaultNamedLabelAiReading(holderId: string) {
  const invalidate = useInvalidateNamedLabels(holderId);
  return useMutation({
    mutationFn: async (labelId: string) =>
      (await api.stopVaultNamedLabelAiReading(holderId, labelId)).label,
    onSuccess: invalidate,
  });
}

/**
 * An owner or admin adding themselves to a label. An owner is added at once
 * (`place`); an admin's waits for another owner or admin (`request`).
 */
export async function recoverVaultNamedLabel(
  holderId: string,
  labelId: string,
  creds: StepUpCredentials,
) {
  return stepUpFetch<{ place: VaultNamedLabelReaderDto } | { request: VaultNamedLabelRequestDto }>(
    `${vaultNamedLabelPath(holderId, labelId)}/recover`,
    { method: 'POST', body: proof(creds) },
  );
}

export async function approveVaultNamedLabelRequest(
  holderId: string,
  requestId: string,
  creds: StepUpCredentials,
) {
  return stepUpFetch<{ request: VaultNamedLabelRequestDto }>(
    `/vault/holders/${encodeURIComponent(holderId)}/named-label-requests/${encodeURIComponent(requestId)}/approve`,
    { method: 'POST', body: proof(creds) },
  );
}
